/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions
} from '@tanstack/react-query';
import {organisationApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {STALE_TIME, GC_TIME} from '#constants';
import {getMerchantsResponseTransformer} from '#utils/response-transformers/merchants';

// Hook for getting suggested organisations
export function useSuggestedOrganisations(
  params: {search?: string},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.organisation.suggested(),
    queryFn: () => organisationApiClient.getSuggested(params),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    enabled: !!params.search, // Only run when search is provided
    ...options
  });
}

// Hook for getting global suggestions
export function useGlobalSuggestions(
  params: {search?: string},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.global(), params],
    queryFn: () => organisationApiClient.getGlobalSuggestions(params),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    enabled: !!params.search, // Only run when search is provided
    ...options
  });
}

export function useOrganisationGlobalSearch(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'global-search', params],
    queryFn: () => organisationApiClient.getGlobalSearchOrganisations(params),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    ...options
  });
}

// Hook for getting organisations
export function useOrganisations(
  params: {search?: string; limit?: number; pageNumber?: number},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.organisation.list(params),
    queryFn: () => organisationApiClient.getOrganisation(params),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    ...options
  });
}

// Hook for getting feed organisations
export function useFeedOrganisations(
  params: Record<string, any>,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.organisation.feed(),
    queryFn: () => organisationApiClient.getFeedOrganisations(params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting user subscriptions
export function useUserSubscriptions(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.user.subscriptions(),
    queryFn: () => organisationApiClient.get('/user/subscription'),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    ...options
  });
}

export function useInfiniteUserSubscriptions(params?: any, options?: any) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.user.subscriptions(), 'infinite'],
    queryFn: async ({pageParam}) => {
      const response = await organisationApiClient.get('/user/subscription', {
        ...params,
        pageNumber: pageParam
      });
      return response as any;
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.pagination.has_more) return lastPage.pagination.page + 1;
      return lastPage.pagination.page;
    },
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    ...options
  });
}

// Hook for getting popular organisations (used in feed)
export function usePopularOrganisations(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.organisation.list({popular: true}),
    queryFn: () => organisationApiClient.getPopular(),
    staleTime: STALE_TIME.ORGANISATION_POPULAR,
    gcTime: GC_TIME.ORGANISATION_POPULAR,
    ...options
  });
}

// Hook for getting organisation by ID
export function useOrganisationById(
  id: string,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>,
  isDemo?: boolean
) {
  return useQuery({
    queryKey: queryKeys.organisation.webshop(id),
    queryFn: () =>
      isDemo
        ? organisationApiClient.getUnauthorizedOrganisationById(id)
        : organisationApiClient.getOrganisationById(id),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    enabled: !!id,
    ...options
  });
}

// Hook for getting organisation details by ID
export function useOrganisationDetailsById(
  id: string,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.organisation.detail(id),
    queryFn: () => organisationApiClient.getOrganisationDetailsById(id),
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    enabled: !!id,
    ...options
  });
}

// Hook for getting organisations by category
export function useOrganisationsByCategory(
  id: number,
  params: {search?: string; limit?: number; pageNumber?: number} = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.byCategory(id), params],
    queryFn: () => organisationApiClient.getOrganisationsByCategory(id, params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    enabled: !!id,
    ...options
  });
}

// Hook for getting all subscriptions (for add subscriptions page)
export function useAllSubscriptions(
  params: {
    search?: string;
    limit?: number;
    categoryId?: number;
    pageNumber?: number;
  } = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.allSubscriptions(), params],
    queryFn: () => organisationApiClient.getAllSubscriptions(params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting organisation suggestions
export function useOrganisationSuggestions(
  params: {search?: string} = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.suggestions(), params],
    queryFn: () => organisationApiClient.getOrganisationSuggestions(params),
    select: data =>
      // @ts-ignore
      data?.data?.suggestions?.map((name: string, index: number) => ({
        id: index,
        name
      })) ?? [],
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    enabled: !!params.search,
    ...options
  });
}

// Hook for getting organisation categories
export function useOrganisationCategories(
  id: string | number,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.detail(id), 'categories'],
    queryFn: async () => {
      const response =
        await organisationApiClient.getOrganisationCategories(id);
      // Apply the same response transformer as RTK implementation
      return (response as any)?.data?.categories?.categories;
    },
    staleTime: STALE_TIME.ORGANISATION,
    gcTime: GC_TIME.ORGANISATION,
    enabled: !!id,
    ...options
  });
}

// Hook for getting organisation category by ID
export function useOrganisationCategoryById(
  id: string | number,
  categoryId: string | number,
  limit?: number,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.detail(id), 'category', categoryId],
    queryFn: () =>
      organisationApiClient.getOrganisationCategoryById(id, categoryId, {
        limit
      }),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    enabled: !!id && !!categoryId,
    ...options
  });
}

// Hook for getting organisation merchants
export function useOrganisationMerchants(
  params: Record<string, any>,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'merchants', params],
    queryFn: async () => {
      const response =
        await organisationApiClient.getOrganisationMerchants(params);
      const {getMerchantsResponseTransformer} =
        await import('#utils/response-transformers/merchants');
      return getMerchantsResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting organisation merchants
export function useInfiniteOrganisationMerchants(
  params: Record<string, any>,
  options?: any
) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.organisation.all, 'merchants', 'infinite', params],
    queryFn: async ({pageParam}) => {
      const response = await organisationApiClient.getOrganisationMerchants({
        ...params,
        pageNumber: pageParam
      });
      return getMerchantsResponseTransformer(response as any);
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.pagination.has_more) return lastPage.pagination.page + 1;
      return lastPage.pagination.page;
    },
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting organisation merchant requests
export function useOrganisationMerchantsRequests(
  params: Record<string, any>,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'merchant-requests', params],
    queryFn: async () => {
      const response =
        await organisationApiClient.getOrganisationMerchantsRequests(params);
      const {getMerchantsResponseTransformer} =
        await import('#utils/response-transformers/merchants');
      return getMerchantsResponseTransformer(response as any);
    },
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting organisation merchant request by ID
export function useOrganisationMerchantRequestById(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'merchant-request', id, params],
    queryFn: () =>
      organisationApiClient.getOrganisationMerchantRequestById(id, params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    enabled: !!id,
    ...options
  });
}

// Hook for getting all merchants
export function useAllMerchants(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'all-merchants', params],
    queryFn: () => organisationApiClient.getAllMerchants(params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting organisation merchant by ID
export function useOrganisationMerchantById(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'merchant', id, params],
    queryFn: () =>
      organisationApiClient.getOrganisationMerchantById(id, params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    enabled: !!id,
    ...options
  });
}

// Hook for getting organisation member by ID
export function useOrganisationMemberById(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'member', id, params],
    queryFn: () => organisationApiClient.getOrganisationMemberById(id, params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    enabled: !!id,
    ...options
  });
}

// Hook for getting organisation members
export function useOrganisationMembers(
  params: Record<string, any>,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'members', params],
    queryFn: () => organisationApiClient.getOrganisationMembers(params),
    ...options
  });
}

// Hook for getting organisation pending members
export function useOrganisationPendingMembers(
  params: Record<string, any>,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'pending-members', params],
    queryFn: () => organisationApiClient.getOrganisationPendingMembers(params),
    ...options
  });
}

// Hook for getting organisation pending merchants
export function useOrganisationPendingMerchants(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'pending-merchants', params],
    queryFn: () =>
      organisationApiClient.getOrganisationPendingMerchants(params),
    staleTime: STALE_TIME.ORGANISATION_FEED,
    gcTime: GC_TIME.ORGANISATION_FEED,
    ...options
  });
}

// Hook for getting organisation invited members
export function useOrganisationInvitedMembers(
  params: Record<string, any>,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.organisation.all, 'invited-members', params],
    queryFn: () => organisationApiClient.getOrganisationInvitedMembers(params),
    ...options
  });
}
